import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { HeaderComponent } from './header/header.component';
import { SportScrollService } from './sport-scroll.service';
import { SiteFooterComponent } from './footer/site-footer.component';
import { MobileNavDrawerComponent } from './header/mobile-nav-drawer.component';
import { LoginModal } from './login-modal/login-modal';
import { SessionService } from './session.service';
import { environment } from '../environments/environment';
import { MatchDataService } from './match-data.service';
import { ToastComponent } from './toast/toast.component';
import { SportsMenuComponent } from './sports-menu/sports-menu.component';
import { BetSlipComponent } from './betslip/betslip.component';
import { MobileShortcutsComponent } from './mobile-shortcuts/mobile-shortcuts.component';
import { BetslipMobileDialogComponent } from './betslip/betslip-mobile-dialog.component';
import { SportsbookFacadeService } from './sportsbook/sportsbook-facade.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, filter, fromEvent } from 'rxjs';
import { AuthDialogService } from './auth-dialog.service';
import { MobileNavigationService } from './mobile-navigation.service';
import { BettingSettingsService } from './betting-settings.service';
import { HeaderMenuService } from './header/header-menu.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    HeaderComponent,
    SiteFooterComponent,
    MobileNavDrawerComponent,
    TranslateModule,
    LoginModal,
    ToastComponent,
    SportsMenuComponent,
    BetSlipComponent,
    MobileShortcutsComponent,
    MatDialogModule
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit {
  private static readonly DESKTOP_RAIL_AUTO_COLLAPSE_BREAKPOINT = 1350;

  readonly headerCompact = signal(false);
  readonly mobileSearchOpen = signal(false);
  readonly interfaceBlocked = computed(() => this.mobileNavigation.isOpen() || this.authDialog.loginOpen());

  private readonly titleService = inject(Title);
  public readonly session = inject(SessionService);
  readonly isMobile = this.session.isMobile;
  private readonly translate = inject(TranslateService);
  public readonly sportsbook = inject(SportsbookFacadeService);
  public readonly authDialog = inject(AuthDialogService);
  private readonly dialog = inject(MatDialog);
  private readonly matchDataService = inject(MatchDataService);
  private readonly bettingSettingsService = inject(BettingSettingsService);
  private readonly mobileNavigation = inject(MobileNavigationService);
  private readonly headerMenu = inject(HeaderMenuService);
  private readonly router = inject(Router);
  private readonly sportScroll = inject(SportScrollService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mainContent = viewChild.required<ElementRef<HTMLElement>>('mainContent');
  private readonly mobileShortcuts = viewChild(MobileShortcutsComponent);
  private readonly currentRouteUrl = signal(this.router.url);
  readonly mobileSearchEnabled = computed(() => this.currentRouteUrl().split(/[?#]/)[0] === '/');
  private lastScrollTop = 0;
  private betSlipDialogRef: MatDialogRef<BetslipMobileDialogComponent> | null = null;
  private desktopRailExpanded: boolean | null = null;
  private initialNavigationHandled = false;

  constructor() {
    effect(onCleanup => {
      const language = this.session.currentLanguage();
      document.documentElement.lang = language;
      const translation = this.translate.use(language).subscribe(() => this.updateDocumentTitle(this.currentRouteUrl()));
      onCleanup(() => translation.unsubscribe());
    });

    effect(() => this.updateDocumentTitle(this.currentRouteUrl()));

    effect(() => {
      if (!this.isMobile()) {
        this.closeMobileBetSlip();
        if (this.mobileSearchOpen()) {
          this.mobileSearchOpen.set(false);
          this.mainContent().nativeElement.focus({ preventScroll: true });
        }
      }
    });

    effect(() => {
      if (this.interfaceBlocked()) {
        this.mobileSearchOpen.set(false);
      }
    });
  }

  retryMatchData(): void {
    void this.matchDataService.loadMatches();
  }

  ngOnInit(): void {
    this.bettingSettingsService.loadSettings();
    this.matchDataService.loadMatches();

    this.updateHeaderCompactState(window.scrollY);
    this.desktopRailExpanded = window.innerWidth >= App.DESKTOP_RAIL_AUTO_COLLAPSE_BREAKPOINT;

    fromEvent(window, 'scroll')
      .pipe(auditTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateHeaderCompactState(window.scrollY));

    fromEvent(window, 'resize')
      .pipe(auditTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const viewportWidth = window.innerWidth;
        this.syncDesktopRailExpansion(viewportWidth);
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationStart | NavigationEnd => event instanceof NavigationStart || event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.mobileSearchOpen.set(false);
          this.authDialog.closeLogin();
          this.mobileNavigation.close();
          this.headerMenu.close();
          this.dialog.closeAll();
          return;
        }

        this.currentRouteUrl.set(event.urlAfterRedirects);

        if (!this.initialNavigationHandled) {
          this.initialNavigationHandled = true;
          return;
        }

        if (!this.sportScroll.filtersRequested()) {
          window.requestAnimationFrame(() => this.mainContent().nativeElement.focus({ preventScroll: true }));
        }
      });
  }

  openMobileBetSlip(): void {
    if (!this.isMobile() || this.betSlipDialogRef) {
      return;
    }

    this.mobileNavigation.close();
    this.mobileSearchOpen.set(false);
    this.betSlipDialogRef = this.dialog.open(BetslipMobileDialogComponent, {
      panelClass: 'betslip-mobile-dialog-panel',
      backdropClass: 'betslip-mobile-dialog-backdrop',
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      ariaLabel: this.translate.instant('betslip.dialogLabel')
    });

    this.betSlipDialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.betSlipDialogRef = null;
        // The invoking shortcut no longer exists after switching to desktop.
        if (!this.isMobile()) {
          this.mainContent().nativeElement.focus({ preventScroll: true });
        } else if (this.sportsbook.betSlipSummary().selectionCount === 0) {
          this.mobileShortcuts()?.focusLive();
        }
      });
  }

  toggleMobileSearch(): void {
    if (this.isMobile() && this.mobileSearchEnabled()) {
      this.mobileSearchOpen.update(open => !open);
    }
  }

  async openMobileLive(): Promise<void> {
    if (!this.mobileSearchEnabled()) {
      await this.router.navigate(['/']);
    }
    this.sportsbook.setActiveFilter('live');
  }

  async openFooterSport(sportId: number): Promise<void> {
    const sport = this.sportsbook.sports().find(item => item.id === sportId);
    if (this.session.matchesLoading() || !sport || sport.totalCount === 0) {
      return;
    }
    if (!this.session.selectedSports().has(sportId)) {
      this.sportsbook.toggleSport(sportId);
    }
    // Reveal an offered sport even when the current time filter or search hides its matches.
    if (!this.sportsbook.hasVisibleSport(sportId)) {
      this.sportsbook.setActiveFilter('all');
      if (!this.sportsbook.hasVisibleSport(sportId)) {
        this.sportsbook.setSearchQuery('');
      }
    }
    this.sportScroll.filtersRequested.set(true);
    if (!this.mobileSearchEnabled() && !await this.router.navigate(['/'])) {
      this.sportScroll.filtersRequested.set(false);
    }
  }

  skipToContent(event: MouseEvent): void {
    // Native fragment navigation also reaches the router and can steal focus
    // from a subsequently opened dialog when its navigation completes.
    event.preventDefault();
    this.mainContent().nativeElement.focus();
  }

  private updateHeaderCompactState(scrollTop: number): void {
    const nextScrollTop = Math.max(scrollTop, 0);
    const delta = nextScrollTop - this.lastScrollTop;

    if (nextScrollTop === 0) {
      this.headerCompact.set(false);
    } else if (delta > 0) {
      this.headerCompact.set(true);
    } else if (delta <= -10) {
      this.headerCompact.set(false);
    } else {
      return;
    }

    this.lastScrollTop = nextScrollTop;
  }

  private closeMobileBetSlip(): void {
    if (!this.betSlipDialogRef) {
      return;
    }

    const dialogRef = this.betSlipDialogRef;
    this.betSlipDialogRef = null;
    dialogRef.close();
  }

  private syncDesktopRailExpansion(viewportWidth: number): void {
    const shouldExpand = viewportWidth >= App.DESKTOP_RAIL_AUTO_COLLAPSE_BREAKPOINT;

    if (this.desktopRailExpanded === shouldExpand) {
      return;
    }

    this.desktopRailExpanded = shouldExpand;
    this.sportsbook.setDesktopSidePanelsExpanded(shouldExpand);
  }

  private updateDocumentTitle(routeUrl: string): void {
    const titleKey = this.pageTitleKey(routeUrl);
    const pageTitle = this.translate.instant(titleKey);
    this.titleService.setTitle(`${pageTitle} | ${environment.brand.title}`);
  }

  private pageTitleKey(routeUrl: string): string {
    if (routeUrl.startsWith('/tickets')) {
      return 'accountTickets.title';
    }

    if (routeUrl.startsWith('/transactions')) {
      return 'accountTransactions.title';
    }

    if (routeUrl.startsWith('/results')) {
      return 'accountResults.title';
    }

    if (routeUrl.startsWith('/match/')) {
      return 'matchDetail.pageTitle';
    }

    return 'home.pageTitle';
  }
}
